import React, { useEffect, useRef, useState } from 'react'
import { StyledLabel, StyledDropdownWrap } from 'components/form/Textfield.styles'
import Button from '@mui/material/Button'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuList from '@mui/material/MenuList'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ServiceListing } from 'features/quote/components/type'
import { CircularProgress } from '@mui/material'
import { CircularSpinnerCss } from 'features/incidents/components/style'
import { OptionsLabel } from 'features/incidents/components/enums'
import { Box } from 'components/elements'
import CSS from 'csstype'

export type Listing = {
  label: string
  value: string | number
  key?: number
  premiumAmount?: number
  maxCoverage?: number
  primaryService?: ServiceListing
  services?: ServiceListing[]
  discount?: number
}

type MenuDropdownProps = {
  label?: string
  value?: Listing
  placeholder?: string
  setValue?: (...arg: any) => void
  list?: Listing[]
  isResponseFetching?: boolean
  isDisabled?: boolean
  /** `light` applies page-scoped classes; styles live in the parent form wrapper, not globally */
  variant?: 'default' | 'light'
  error?: boolean
  style?: CSS.Properties
  inputStyle?: CSS.Properties
}

export const MenuDropdown = ({
  placeholder,
  label,
  value,
  setValue,
  list,
  isResponseFetching,
  isDisabled,
  variant = 'default',
  error = false,
  style,
  inputStyle,
}: MenuDropdownProps) => {
  const [open, setOpen] = useState(false)
  const [filteredList, setFilteredList] = useState<Listing[] | undefined>(list)
  const [filterValue, setFilterValue] = useState<string>('')
  const anchorRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isLight = variant === 'light'

  const wrapClassName = [isLight ? 'menu-dropdown-light' : '', isLight && error ? 'menu-dropdown-light--error' : ''].filter(Boolean).join(' ') || undefined

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
    setFilterValue('')
  }

  useEffect(() => {
    if (!filterValue.trim()) {
      setFilteredList(list)
    } else {
      const filtered = list?.filter(item => item?.label.toLowerCase().includes(filterValue.trim().toLowerCase()))
      setFilteredList(filtered?.length ? filtered : list)
    }
  }, [filterValue, list])

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open) {
      inputRef?.current?.focus()
    }
  }, [open])

  // Light panel styles are inline so they win over global `.dropdownWidth` / `.dropdownList` rules
  const lightPaperSx = {
    backgroundColor: 'var(--colors-white)',
    border: '1px solid var(--colors-gray13)',
    borderRadius: '4px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  }

  const lightMenuListSx = {
    padding: '.75rem 0',
    maxHeight: 280,
    overflowY: 'auto',
    backgroundColor: 'var(--colors-white)',
  }

  return (
    <StyledDropdownWrap className={wrapClassName}>
      <StyledLabel css={{ color: isLight ? '$black39' : '$gray5', fontWeight: isLight ? '$medium' : '$normal' }}>{label}</StyledLabel>
      <Button
        disabled={isDisabled}
        ref={anchorRef}
        id="fade-button"
        aria-controls={open ? 'fade-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleToggle}
        className="main"
        style={{ ...inputStyle, wordBreak: 'break-all' }}
        endIcon={
          isResponseFetching ? <CircularProgress size={12} sx={CircularSpinnerCss} /> : <KeyboardArrowDownIcon sx={isLight ? { color: 'var(--colors-black39)' } : undefined} />
        }
      >
        <Box as="span" className={isLight ? (value?.label ? 'menu-dropdown-light__value' : 'menu-dropdown-light__placeholder') : undefined}>
          {value?.label || placeholder}
        </Box>
      </Button>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        // Do not use global `dropdownWidth` for light variant — it forces left:0 and breaks layout
        className={isLight ? 'light-dropdown-menu' : 'dropdownWidth'}
        style={{
          ...style,
          zIndex: isLight ? 20 : style?.zIndex,
          width: isLight && anchorRef.current ? anchorRef.current.offsetWidth : style?.width,
        }}
      >
        {({ TransitionProps, placement: popperPlacement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: popperPlacement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper className={isLight ? 'light-dropdown-paper' : undefined} sx={isLight ? lightPaperSx : undefined}>
              <input type="text" placeholder="Search" className="dropdown-hidden" value={filterValue} onChange={e => setFilterValue(e.target.value)} ref={inputRef} />
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  sx={
                    isLight
                      ? { ...lightMenuListSx, overflowX: 'hidden', wordBreak: 'break-all' }
                      : { padding: '.75rem 0', borderRadius: '.5rem', overflowX: 'hidden', wordBreak: 'break-all' }
                  }
                >
                  {filteredList && filteredList.length > 0 ? (
                    filteredList.map((item, index) => (
                      <li
                        key={index}
                        onClick={e => {
                          handleClose(e)
                          setValue &&
                            setValue({
                              label: item?.label,
                              value: item?.value,
                              key: item?.key,
                              premiumAmount: item?.premiumAmount || null,
                              maxCoverage: item?.maxCoverage || null,
                              primaryService: item?.primaryService || null,
                              services: item?.services || null,
                              discount: item?.discount,
                            })
                        }}
                        className={isLight ? 'light-dropdownList' : 'dropdownList'}
                        style={
                          isLight
                            ? {
                                color: 'var(--colors-black39)',
                                padding: '.75rem 1rem',
                                cursor: 'pointer',
                                listStyle: 'none',
                                fontSize: '1rem',
                              }
                            : undefined
                        }
                      >
                        {item?.label}
                      </li>
                    ))
                  ) : (
                    <Box as="li" className={isLight ? 'light-dropdownList' : 'dropdownList'} aria-disabled css={{ cursor: 'not-allowed' }}>
                      {OptionsLabel.NO_OPTIONS}
                    </Box>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </StyledDropdownWrap>
  )
}
